import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper'; // Adjust the import path as necessary
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import {
  Autocomplete,
  Box,
  Chip,
  InputAdornment,
  ListSubheader,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { buildTreeData } from '../utils/energyTreeFunctions';

const EnergyTreeNodeAutocomplete = ({
  nodeOptions = [],
  selectedNode,
  onChange,
  label = 'Vælg node',
  placeholder = 'Vælg node',
}) => {
  const [hoveredPath, setHoveredPath] = useState([]);
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const options = useMemo(() => {
    if (!nodeOptions?.length) return [];

    return buildTreeData(nodeOptions);
  }, [nodeOptions]);

  return (
    <Autocomplete
      id="tree-node-autocomplete"
      fullWidth
      options={options || []}
      value={selectedNode || null}
      isOptionEqualToValue={(option, value) =>
        option.energyTreeNodeId === value.energyTreeNodeId
      }
      getOptionLabel={(option) => option.name}
      onChange={(event, newValue) => onChange(newValue)}
      groupBy={() => 'single-group'}
      filterOptions={(options, { inputValue }) => {
        const lowerInput = inputValue.toLowerCase();
        return options.filter((option) =>
          option._path?.some((n) => n.name.toLowerCase().includes(lowerInput)),
        );
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <ListSubheader sx={{ bgcolor: 'transparent' }}>
            <Chip
              variant="filled"
              size="large"
              sx={{
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#121212' : '#FFFFFF',
                border: '1px solid',
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
              }}
              label={hoveredPath.map((n) => n.name).join(' / ') || 'Ingen sti'}
            />
          </ListSubheader>
          {params.children}
        </li>
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.energyTreeNodeId}
          onMouseEnter={() => setHoveredPath(option._path || [])}
          style={{ paddingLeft: `${option._path?.length * 24}px` }}
        >
          <Box
            sx={{
              backgroundColor: getTypeColor(option?.type),
              position: 'relative', // Changed from 'absolute' to maintain layout
              borderRadius: '25px',
              width: '28px',
              height: '28px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: `1px solid divider`,
              marginRight: 1,
            }}
          >
            <i
              style={{
                color: 'white',
              }}
              className={`fa-solid ${getTypeIcon(option?.type)}`}
            ></i>
          </Box>
          {option.name}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          placeholder={placeholder}
          variant="standard"
          sx={{
            '& .MuiFilledInput-root': {
              '&:before': { borderBottom: 'none' },
              '&:after': { borderBottom: 'none' },
              '&:hover:before': { borderBottom: 'none !important' },
              borderRadius: 1,
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <UnfoldMoreRoundedIcon />
                <Box
                  sx={{
                    backgroundColor: getTypeColor(selectedNode?.type),
                    position: 'relative', // Changed from 'absolute' to maintain layout
                    borderRadius: '25px',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: `1px solid divider`,
                    ml: 1,
                  }}
                >
                  <i
                    style={{
                      color: 'white',
                    }}
                    className={`fa-solid ${getTypeIcon(selectedNode?.type)}`}
                  ></i>
                </Box>
              </InputAdornment>
            ),
            endAdornment: <InputAdornment position="end"></InputAdornment>,
            disableUnderline: true,
          }}
        />
      )}
      slotProps={{
        popper: {
          placement: isLgUp ? 'left' : 'bottom', // try 'left', 'left-start', 'left-end'

          sx: {
            minWidth: 600, // ensure Paper fills the Popper width

            zIndex: (t) => t.zIndex.modal + 1,
          },
        },
        listbox: {
          sx: { maxHeight: 360 }, // optional: list height separate from Paper
        },
      }}
    />
  );
};

export default EnergyTreeNodeAutocomplete;
